import { buildSearchQuery } from '@/utils/search/searchQuery';
import { changePaginationPageSize, getSearchPageSize } from '@/redux/modules/pagination';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { FilterAction } from '@/types/SearchFacet';
import { formatLocationString } from '@/utils/formatLocationFromSlug';
import {
    getIsCollectionsPage,
    getIsSearchPage,
    getIsSEOSearchPage,
    getIsSEOSearchPageWithLocation,
} from '@/utils/urls';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { getSEOSearchTermData } from '@/redux/modules/search/search.selectors';
import { getStateName } from '@/utils/address';
import { HowToWinBannerContainer } from '@/components/HowToWinBanner';
import { isEmpty } from '@/utils/isEmpty/isEmpty';
import { MarketingEventBanner } from './components/MarketingEventBanner/MarketingEventBanner';
import {
    modifyParamsRemovePage,
    modifyParamsWithPage,
    modifyParamsWithPageSize,
    parseQueryParams,
} from '@/utils/queryParams';
import { PAGE_ITEMS_TO_VIEW } from '@/components/Pagination/helpers/filterSortOptions';
import { RelatedSearches } from '@/components/RelatedSearches/RelatedSearches';
import { RelatedSearchesSuggestions } from './components/RelatedSearchesSuggestions/RelatedSearchesSuggestions';
import { SearchPageHeader } from './components/SearchPageHeader/SearchPageHeader';
import { SearchPageHeaderMetaWrapper } from './components/SearchPageHeaderMetaWrapper/SearchPageHeaderMetaWrapper';
import { SearchPageSeoTitleAndCopy } from './components/SearchPageSeoContent/SearchPageSeoTitleAndCopy';
import { SearchPageTabs } from './components/SearchPageTabs/SearchPageTabs';
import { SearchPageZeroResultsExperiment } from './components/SearchPageZeroResults/SearchPageZeroResultsExperiment';
import { SearchResultsAdContainer } from '@/components/Ads/SearchResultsAd/SearchResultsAdContainer';
import { SearchResultsBreadcrumbs } from './components/SearchResultsBreadcrumbs/SearchResultsBreadcrumbs';
import { SeeWhatsPopularSection } from './components/SeeWhatsPopularSection/SeeWhatsPopularSection';
import { TrackPageBottomReachedTrigger } from '@/utils/amplitude/analytics/pageBottomReached/TrackPageBottomReachedTrigger';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useAuctionHouseSearchFilteringExperiment } from '@/utils/amplitude/experiments/hooks/useAuctionHouseSearchFiltering';
import { useGetAppliedFilterChipValues } from './hooks/useGetAppliedFilterChipValues';
import { useGetSearchPageCanonicalUrl } from './hooks/useGetSearchPageCanonicalUrl';
import { useHandleRefetchOnExcludedHouse } from './hooks/useHandleRefetchOnExcludedHouse';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useNavigateSearchPageWithParams } from './hooks/useNavigateSearchPageWithParams';
import { useOptimizedSearchSelectors } from '@/hooks/useOptimizedSearchSelectors';
import { usePredictiveAverageLotValueInElastic } from '@/utils/amplitude/experiments/hooks/usePredictiveAverageLotValueInElastic';
import { useRedirectToValidPage } from './hooks/useRedirectToValidPage';
import { useRelatedSearchesOnSrpsExperiment } from '@/utils/amplitude/experiments/hooks/useRelatedSearchesOnSrpsExperiment';
import { useZeroResultsSearchPageExperiment } from '@/utils/amplitude/experiments/hooks/useZeroResultsSearchPageExperiment';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import paramTypes from '@/types/search/enums/paramTypes';
import SearchError from '@/components/SearchError/SearchError';

const SearchPageSimplified = () => {
    const { pathname, search } = useLocation();

    const isCollectionsPage = getIsCollectionsPage(pathname);
    const isOnSearchPage = getIsSearchPage(pathname);
    const isSEOSearchPage = getIsSEOSearchPage(pathname) || getIsSEOSearchPageWithLocation(pathname);
    const queryParams = parseQueryParams(search);

    const pageSize = useAppSelector(getSearchPageSize);
    const seoSearchTermData = useAppSelector(getSEOSearchTermData);
    const dispatch = useAppDispatch();

    // Use optimized selectors to batch Redux calls and reduce re-renders
    const {
        archivedSearch: isArchivedSearchPage,
        rawFacets,
        searchState,
    } = useOptimizedSearchSelectors(queryParams, pathname);
    const {
        error: searchFailed,
        isInitialLoading,
        isZeroResults,
        submitted: isLoadingSearchResults,
        totalFound,
        totalPages,
        totalSoldItems,
    } = searchState;

    useGetAppliedFilterChipValues();

    usePredictiveAverageLotValueInElastic();

    // Experiment: sear-701-auction-house-search-filtering
    useAuctionHouseSearchFilteringExperiment();

    const navigateSearchPageWithParams = useNavigateSearchPageWithParams();

    const searchQuery = useMemo(() => {
        return buildSearchQuery({
            pathname,
            queryParams,
            rawFacets,
            searchPageSize: pageSize,
            similarItems: true,
        });
    }, [pathname, queryParams, rawFacets, pageSize]);
    const { citySlug, distance, page, ranges, region, searchTerm } = searchQuery;

    const displaySearchTerm = useMemo(() => {
        return seoSearchTermData?.term || cleanSearchTerms(searchTerm);
    }, [seoSearchTermData?.term, searchTerm]);

    const displayLocation = useMemo(() => {
        const locationString = formatLocationString(citySlug, region, getStateName);
        return locationString ? ` ${locationString}` : '';
    }, [citySlug, region]);

    // if this a SEO page, we need to add the keyword to the save search params from the slug.
    const saveSearchParams = getSaveSearchParams({ ...queryParams, keyword: searchTerm });

    useHandleRefetchOnExcludedHouse({ searchQuery });

    const hasRelatedSearchesTreatment = useRelatedSearchesOnSrpsExperiment();
    const hasZeroResultsSearchPageTreatment = useZeroResultsSearchPageExperiment();
    const showZeroResults = isZeroResults && hasZeroResultsSearchPageTreatment;

    const shouldShowRelatedSearchesSuggestions = searchTerm && hasRelatedSearchesTreatment;

    // Redirect to page 1 if requested page exceeds totalPages (e.g. /search/ping/?page=1000 but totalPages is 4)
    useRedirectToValidPage({
        isInitialLoading,
        isLoadingSearchResults,
        page,
        queryParams,
        totalPages,
    });

    const navigateWithParams = (params: any, filterAction: FilterAction) => {
        let newParams = params;
        if (params.page === 1) {
            newParams = modifyParamsRemovePage(newParams);
        }
        navigateSearchPageWithParams({ filterAction, params: newParams });
    };

    const changePageAndNavigate = (selected: number) => {
        const page = selected + 1;
        const newParams = modifyParamsWithPage(queryParams, page);
        navigateWithParams(newParams, FilterAction.PageChanged);
    };

    const changePageSizeAndNavigate = (pageSize: number) => {
        dispatch(changePaginationPageSize(pageSize));
        changePaginationPageSize(pageSize);
        const newParams = modifyParamsWithPageSize(queryParams, pageSize);
        navigateWithParams(newParams, FilterAction.PageSizeChanged);
    };

    const onChange = ({ page, pageSize }: { page: number; pageSize: number }) => {
        if (!Boolean(pageSize)) {
            changePageAndNavigate(page - 1);
        } else {
            changePageSizeAndNavigate(pageSize);
        }
    };

    let canonicalURL = useGetSearchPageCanonicalUrl();

    const { hasNoFilterTermOrRangeResults, hasNoKeywordSearchResults } = useMemo(() => {
        const filterTermQuery = queryParams[paramTypes.FILTER_TERM] || null;
        const filterTerms: string[] = filterTermQuery ? filterTermQuery.split(',') : [];

        const isLoading = isLoadingSearchResults || isInitialLoading;
        const hasNoResults = totalFound === 0 && !isLoading;
        const hasNoFilterTermOrRangeResults =
            (Boolean(filterTerms.length) || Boolean(distance?.zipCode) || !isEmpty(ranges) || totalSoldItems > 0) &&
            hasNoResults;

        const hasNoKeywordSearchResults = !hasNoFilterTermOrRangeResults && hasNoResults && totalSoldItems === 0;

        return { hasNoFilterTermOrRangeResults, hasNoKeywordSearchResults };
    }, [distance?.zipCode, isInitialLoading, isLoadingSearchResults, queryParams, ranges, totalFound, totalSoldItems]);

    return (
        <div>
            {isCollectionsPage && (
                <>
                    <div className="m-auto max-w-[1644px] px-md xsMax:px-sm">
                        <SearchResultsBreadcrumbs />
                    </div>
                    <MarketingEventBanner isArchivedSearchPage={isArchivedSearchPage} />
                </>
            )}

            <div
                className={cn('m-auto max-w-[1644px] px-md py-md xsMax:px-sm xsMax:py-sm', {
                    'pt-sm': isCollectionsPage,
                    'pt-xs': isSEOSearchPage,
                })}
            >
                <SearchPageHeaderMetaWrapper
                    canonicalURL={canonicalURL}
                    displayLocation={displayLocation}
                    displaySearchTerm={displaySearchTerm}
                    isCollectionsPage={isCollectionsPage}
                    isSEOSearchPage={isSEOSearchPage}
                    page={page}
                    searchTerm={searchTerm}
                    totalFound={totalFound}
                />

                {isSEOSearchPage && <SearchResultsBreadcrumbs className="pb-sm" />}
                {isOnSearchPage && <HowToWinBannerContainer />}
                {isOnSearchPage && <CompleteAccountBanner className="mb-lg mt-xs max-w-full xsMax:mb-md xsMax:mt-xs" />}
                {searchFailed ? (
                    <SearchError />
                ) : showZeroResults ? (
                    <SearchPageZeroResultsExperiment
                        canonicalURL={canonicalURL}
                        distance={distance}
                        hasNoFilterTermOrRangeResults={hasNoFilterTermOrRangeResults}
                        isArchivedSearch={isArchivedSearchPage}
                        isSEOSearchPage={isSEOSearchPage}
                        onChange={onChange}
                        page={page}
                        pageSize={pageSize}
                        saveSearchParams={saveSearchParams}
                        searchTerm={searchTerm}
                        totalFound={totalFound}
                        totalPages={totalPages}
                        totalSoldItems={totalSoldItems}
                    />
                ) : (
                    <>
                        <SearchPageHeader
                            displayLocation={displayLocation}
                            displaySearchTerm={displaySearchTerm}
                            hasNoKeywordSearchResults={hasNoKeywordSearchResults}
                            isArchivedSearch={isArchivedSearchPage}
                            isCollectionsPage={isCollectionsPage}
                            isLoadingSearchResults={isLoadingSearchResults || isInitialLoading}
                            isSEOSearchPage={isSEOSearchPage}
                            saveSearchParams={saveSearchParams}
                            searchTerm={searchTerm}
                            totalFound={totalFound}
                            totalSoldItems={totalSoldItems}
                        />

                        {hasNoKeywordSearchResults ? (
                            // NO RESULTS
                            <SeeWhatsPopularSection />
                        ) : (
                            <>
                                {shouldShowRelatedSearchesSuggestions && <RelatedSearchesSuggestions />}
                                <Pagination
                                    canonicalUrl={canonicalURL}
                                    onChange={onChange}
                                    pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                                    paginationFilter={{ page, pageSize, totalRecords: totalFound }}
                                    totalPages={totalPages}
                                >
                                    <SearchPageTabs
                                        distance={distance}
                                        hasNoFilterTermOrRangeResults={hasNoFilterTermOrRangeResults}
                                        isArchivedSearch={isArchivedSearchPage}
                                        isSEOSearchPage={isSEOSearchPage}
                                        searchTerm={searchTerm}
                                        totalSoldItems={totalSoldItems}
                                    />
                                </Pagination>
                                {isSEOSearchPage && seoSearchTermData && (
                                    <>
                                        <SearchPageSeoTitleAndCopy seoSearchTermData={seoSearchTermData} />
                                        <RelatedSearches
                                            containerClasses="mx-auto mt-lg max-w-[1240px] p-sm smMin:p-lg"
                                            headingMessageId="searchPage.seoPage.relatedSearches"
                                            labelKey="label"
                                            relatedTerms={seoSearchTermData?.relatedTerms || []}
                                            urlKey="url"
                                        />
                                    </>
                                )}
                                <TrackPageBottomReachedTrigger />
                                <SearchResultsAdContainer
                                    page={page}
                                    searchTerm={searchTerm}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchPageSimplified;
